import { FormControl, Button, Flex } from "@chakra-ui/react";
import { useUserData } from "../../hooks/useUserData";
import { Avatar, AvatarSize } from "../user/Avatar";
import { SendIcon } from "../icons/SendIcon";
import { ContentEditable } from "../text/ContentEditable";
import { RefObject, useEffect, useRef, useState } from "react";
import { CloseCircleIcon } from "../icons/CloseCircleIcon";
import { useMobileBreakpoint } from "../../utils/useBreakpoints";

export function SubmitForm({
  text,
  isDisabled = false,
  isSmall: _isSmall = false,
  noAvatar = false,
  avatarSize,
  placeholder,
  handleSubmit,
  handleClear,
  clearOnSubmit = true,
  autoFocus,
  formRef,
}: {
  text?: string;
  isDisabled?: boolean;
  isSmall?: boolean;
  noAvatar?: boolean;
  avatarSize?: AvatarSize;
  placeholder?: string;
  handleSubmit: (message: string) => void;
  handleClear?: () => void;
  clearOnSubmit?: boolean;
  autoFocus?: boolean;
  formRef?: RefObject<HTMLDivElement>;
}) {
  const { userData } = useUserData();
  const isMobile = useMobileBreakpoint();
  const editableRef = useRef<HTMLDivElement>(null);
  const [hasContent, setHasContent] = useState(false);

  const ref = formRef || editableRef;
  useEffect(() => {
    if (autoFocus && ref.current) {
      ref.current.focus();
    }
  }, [autoFocus]);

  useEffect(() => {
    const el = ref.current;
    if (el && text) {
      setHasContent(true);
      el.innerHTML = text ? text + "&nbsp;" : "";
      focusEndContent(el);
    }
  }, [text]);

  const focusEndContent = (el: HTMLDivElement) => {
    el.focus();

    // Move cursor to end when editing
    const range = document.createRange();
    range.selectNodeContents(el);
    range.collapse(false);

    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(range);

    el.scrollIntoView({ behavior: "smooth", block: "nearest" });
  };

  const onHandleSubmit = (el: HTMLElement) => {
    const innerText = el.innerText.trim();

    if (innerText && innerText !== placeholder) {
      handleSubmit(innerText);
      if (clearOnSubmit) {
        el.innerText = "";
        setHasContent(false);
        el.focus();
      }
    }
  };

  return (
    <FormControl
      as="form"
      label="Chat Input Form"
      onSubmit={(e) => {
        e.preventDefault();
        if (ref.current) onHandleSubmit(ref.current);
      }}
      onFocus={(e) => {
        e.preventDefault();
        if (ref.current) focusEndContent(ref.current);
      }}
      position="relative"
      isDisabled={isDisabled}
    >
      <Flex
        minHeight={isMobile ? "56px" : "80px"}
        height="fit-content"
        width="100%"
        _focusWithin={{
          borderColor: "transparent.white.40",
        }}
        px="16px"
        background="transparent.white.05"
        border="1px solid"
        borderColor="transparent.white.20"
        borderRadius="24px"
        transition="all .2s linear"
        boxShadow="none"
        outline="none"
        justifyContent="space-between"
        alignItems="center"
      >
        <Flex flex="1" gap="12px" paddingRight="12px" alignItems="center">
          {!noAvatar && (
            <Avatar
              size={avatarSize ?? (isMobile ? "md" : "lg")}
              isGold={userData?.gold}
              username={userData?.name}
              imageSrc={userData?.avatar_tiny_url}
            />
          )}
          <ContentEditable
            passedEditableRef={formRef || editableRef}
            enabled={!isDisabled}
            handleSubmit={(e) => onHandleSubmit(e.currentTarget)}
            onFocus={(e) => {
              const innerText = e.currentTarget.innerText.trim();
              if (innerText === placeholder) {
                e.currentTarget.innerText = "";
              }
              setHasContent(!!e.currentTarget.innerText.trim());
            }}
            onBlur={(e) => {
              const innerText = e.currentTarget.innerText.trim();
              if (!innerText) {
                e.currentTarget.innerText = placeholder ?? "";
                setHasContent(false);
              }
            }}
            onInput={(e) => {
              const innerText = e.currentTarget.innerText.trim();
              setHasContent(!!innerText);
            }}
            color={hasContent ? "dune.100" : "dune.600"}
            fontFamily="Roboto"
            fontSize="16px"
            fontWeight="500"
            lineHeight="130%"
            flex="1"
            height="fit-content"
            outline="none"
            dangerouslySetInnerHTML={{
              __html: text ?? placeholder ?? "",
            }}
          />
        </Flex>
        <Flex gap="12px">
          {handleClear && (
            <Button
              borderRadius={isMobile ? "12px" : "20px"}
              boxSize={isMobile ? "32px" : "48px"}
              aria-label="send message"
              minW={isMobile ? "32px" : undefined}
              padding={isMobile ? "0px" : undefined}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleClear();
                if (editableRef.current) {
                  editableRef.current.innerText = "";
                  setHasContent(false);
                  editableRef.current.focus();
                }
              }}
            >
              <CloseCircleIcon boxSize={isMobile ? "20px" : "24px"} />
            </Button>
          )}
          <Button
            type="submit"
            borderRadius={isMobile ? "12px" : "20px"}
            boxSize={isMobile ? "32px" : "48px"}
            aria-label="send message"
            background={hasContent ? "success.700" : "transparent.white.10"}
            minW={isMobile ? "32px" : undefined}
            padding={isMobile ? "0px" : undefined}
            _hover={{
              background: hasContent ? "success.800" : "transparent.white.20",
            }}
          >
            <SendIcon boxSize={isMobile ? "20px" : "24px"} />
          </Button>
        </Flex>
      </Flex>
    </FormControl>
  );
}
